import { Button, CheckboxFilter, Glyphs, SelectFilter, Tooltip, ViewToggle } from '@/ui'
import { useWhatsAppEnabled } from '@/ui/utilities/hooks/use-whatsapp-enabled'
import ctl from '@netlify/classnames-template-literals'
import { CheckIcon, LucideIcon, MailIcon, MessageCircleIcon, PencilIcon, RotateCcwIcon } from 'lucide-react'
import { ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

export type ViewMode = 'table' | 'swimlanes'

type SortField = 'name' | 'score' | 'applied'
type SortDirection = 'asc' | 'desc'
export type SortOption = `${SortField}-${SortDirection}`

function UnreadBadge({ count, tooltipText, icon: Icon, color }: {
	count: number
	tooltipText: string
	icon: LucideIcon
	color: 'blue' | 'green'
}): ReactNode {
	if (count <= 0) { return null }

	return (
		<Tooltip content={tooltipText}>
			<span
				className={ctl(`
				inline-flex items-center gap-1.5 px-2.5 py-1 text-sm font-medium rounded-full
				${color === 'blue' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}
			`)}
			>
				<Icon className="size-4" />
				{count}
			</span>
		</Tooltip>
	)
}

export interface CampaignCandidatesToolbarProps {
	viewMode: ViewMode
	onViewModeChange: (mode: ViewMode) => void
	sortOption: SortOption
	onSortChange: (option: SortOption) => void
	groupByStages: boolean
	onGroupByStagesChange: (grouped: boolean) => void
	isRefreshing: boolean
	onRefresh: () => void
	canExport: boolean
	onExport: () => void
	unreadEmailCount?: number
	unreadWhatsAppCount?: number
	editMode?: boolean
	onEditModeChange?: (editing: boolean) => void
	canEditPipeline?: boolean
	onResetToDefaults?: () => void
}

export function CampaignCandidatesToolbar({
	viewMode,
	onViewModeChange,
	sortOption,
	onSortChange,
	groupByStages,
	onGroupByStagesChange,
	isRefreshing,
	onRefresh,
	canExport,
	onExport,
	unreadEmailCount = 0,
	unreadWhatsAppCount = 0,
	editMode = false,
	onEditModeChange,
	canEditPipeline = false,
	onResetToDefaults,
}: CampaignCandidatesToolbarProps) {
	const { t } = useTranslation('common')
	const isWhatsappEnabled = useWhatsAppEnabled()

	const sortOptions: Array<{ id: SortOption; value: SortOption; label: string }> = useMemo(() => [
		{ id: 'name-asc', value: 'name-asc', label: t('toolbar.sort.nameAsc') },
		{ id: 'name-desc', value: 'name-desc', label: t('toolbar.sort.nameDesc') },
		{ id: 'score-desc', value: 'score-desc', label: t('toolbar.sort.scoreDesc') },
		{ id: 'score-asc', value: 'score-asc', label: t('toolbar.sort.scoreAsc') },
		{ id: 'applied-desc', value: 'applied-desc', label: t('toolbar.sort.appliedDesc') },
		{ id: 'applied-asc', value: 'applied-asc', label: t('toolbar.sort.appliedAsc') },
	], [t])

	return (
		<div className="sticky top-0 z-10 flex gap-2 items-center px-4 py-4 text-sm bg-white border-b border-gray-200">
			<UnreadBadge
				count={unreadEmailCount}
				tooltipText={t('candidates.unreadEmails', { count: unreadEmailCount })}
				icon={MailIcon}
				color="blue"
			/>
			{isWhatsappEnabled && (
				<UnreadBadge
					count={unreadWhatsAppCount}
					tooltipText={t('candidates.unreadWhatsApp', { count: unreadWhatsAppCount })}
					icon={MessageCircleIcon}
					color="green"
				/>
			)}
			{viewMode === 'table' && (
				<CheckboxFilter
					checked={groupByStages}
					onChange={onGroupByStagesChange}
					label={t('toolbar.groupByStages')}
				/>
			)}
			<SelectFilter
				value={sortOption}
				onChange={(value: string) => {
					if (sortOptions.some(opt => opt.value === value)) {
						onSortChange(value as SortOption)
					}
				}}
				options={sortOptions}
			/>
			<div className="grow" />
			<ViewToggle
				value={viewMode}
				onChange={onViewModeChange}
				options={[{
					value: 'swimlanes',
					label: t('toolbar.swimlanes'),
					icon: <Glyphs.core.BoardIcon />,
				}, {
					value: 'table',
					label: t('toolbar.table'),
					icon: <Glyphs.core.TableIcon />,
				}]}
			/>
			{viewMode === 'swimlanes' && canEditPipeline && onEditModeChange && (
				<>
					{editMode && onResetToDefaults && (
						<Tooltip content={t('toolbar.resetToDefaultsTooltip')}>
							<Button
								onClick={onResetToDefaults}
								type="subtle"
								size="short"
							>
								<RotateCcwIcon className="size-4 mr-1" />
								{t('toolbar.resetToDefaults')}
							</Button>
						</Tooltip>
					)}
					<Button
						onClick={() => onEditModeChange(!editMode)}
						type={editMode ? 'committal' : 'subtle'}
						size="short"
					>
						{editMode
							? (
								<>
									<CheckIcon className="size-4 mr-1" />
									{t('toolbar.done')}
								</>
							)
							: (
								<>
									<PencilIcon className="size-4 mr-1" />
									{t('toolbar.editPipeline')}
								</>
							)}
					</Button>
				</>
			)}
			<Button
				onClick={onRefresh}
				isLoading={isRefreshing}
				type="subtle"
				size="short"
				prefix="refresh"
			>
				{t('toolbar.refresh')}
			</Button>
			<Button
				disabled={!canExport}
				onClick={onExport}
				type="subtle"
				size="short"
			>
				{t('toolbar.exportCsv')}
			</Button>
		</div>
	)
}
